import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";

const DAY_START_HOUR = 6;
const NIGHT_START_HOUR = 18;

function shouldUseDarkTheme(date = new Date()) {
  const hour = date.getHours();
  return hour < DAY_START_HOUR || hour >= NIGHT_START_HOUR;
}

function getMillisecondsUntilNextThemeChange(date = new Date()) {
  const next = new Date(date);
  const hour = date.getHours();

  if (hour < DAY_START_HOUR) {
    next.setHours(DAY_START_HOUR, 0, 0, 0);
  } else if (hour < NIGHT_START_HOUR) {
    next.setHours(NIGHT_START_HOUR, 0, 0, 0);
  } else {
    next.setDate(next.getDate() + 1);
    next.setHours(DAY_START_HOUR, 0, 0, 0);
  }

  return next.getTime() - date.getTime();
}

function applyTheme(isDark: boolean) {
  document.documentElement.classList.toggle("dark", isDark);
  document.documentElement.style.colorScheme = isDark ? "dark" : "light";
}

export function useTheme() {
  const [dark, setDark] = useState(false);

  useEffect(() => {
    let timeoutId: number;

    const scheduleAutoTheme = () => {
      const isDark = shouldUseDarkTheme();
      setDark(isDark);
      applyTheme(isDark);
      timeoutId = window.setTimeout(scheduleAutoTheme, getMillisecondsUntilNextThemeChange());
    };

    scheduleAutoTheme();

    return () => window.clearTimeout(timeoutId);
  }, []);

  const toggle = () => {
    setDark((d) => {
      const next = !d;
      applyTheme(next);
      return next;
    });
  };

  return { dark, toggle };
}

export function ThemeToggle() {
  const { dark, toggle } = useTheme();
  return (
    <button
      aria-label="Toggle theme"
      onClick={toggle}
      className="grid h-10 w-10 place-items-center rounded-full border border-border/60 bg-background/40 text-foreground transition hover:bg-background/80"
    >
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}
